import React, { useEffect } from 'react';
import confetti from 'canvas-confetti';
import { 
  FaCheckCircle, 
  FaKey, 
  FaCalendarAlt, 
  FaUserShield, 
  FaShareAlt, 
  FaExternalLinkAlt, 
  FaMedal 
} from 'react-icons/fa';

export default function ProofOfWorkVault({ verifiedPayload, onBackToHub }) {
  
  // Trigger immediate high-signal celebration confetti upon entering the page
  useEffect(() => {
    const duration = 2 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 1000 };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
    }, 250);

    return () => clearInterval(interval);
  }, []);

  // Safe evaluation anchors in case previous component values are empty strings
  const displayDomain = verifiedPayload?.domain || "Backend Systems";
  const displayTool = verifiedPayload?.tool || "Knex.js";
  const displaySkill = verifiedPayload?.skill || "LLM Structured Extraction";
  const displayRawText = verifiedPayload?.text || "Optimized PostgreSQL vector embedding pipeline using custom batching chunks, cutting operational latency by 64%.";

  return (
    <div style={styles.container}>
      <div style={styles.vaultWrapper}>
        
        {/* Top Success Branding Alert Strip */}
        <div style={styles.successBanner}>
          <FaCheckCircle style={styles.mainCheckIcon} />
          <h2 style={styles.bannerHeading}>Proof-of-Work Entry Cryptographically Verified</h2>
          <p style={styles.bannerSubtext}>Added to decentralized skill ledger with block state index #418A2</p>
        </div>

        {/* Core Block Metadata Layout Matrix */}
        <div style={styles.ledgerInfoCard}>
          <div style={styles.infoRow}>
            <div style={styles.infoBlock}>
              <span style={styles.infoLabel}><FaUserShield /> Verifier Authority</span>
              <span style={styles.infoValue}>live-resume AI Engine v1.4</span>
            </div>
            <div style={styles.infoBlock}>
              <span style={styles.infoLabel}><FaKey /> Consensus Hash</span>
              <span style={styles.infoValueCode}>sha256_9c2b_7ef4...</span>
            </div>
            <div style={styles.infoBlock}>
              <span style={styles.infoLabel}><FaCalendarAlt /> Confirmed Block Time</span>
              <span style={styles.infoValue}>May 22, 2026 - 16:35 UTC</span>
            </div>
          </div>
        </div>

        {/* Verified Payload Body Panels */}
        <div style={styles.splitDetailsGrid}>
          
          {/* Left Column: Original Task Commit Assertion Statement */}
          <div style={styles.detailPanelCard}>
            <h3 style={styles.panelTitle}>Committed Task Assertion</h3>
            <div style={styles.assertionBox}>
              <p style={styles.assertionText}>"{displayRawText}"</p>
            </div>
            <div style={styles.taxonomyPillsRow}>
              <span style={styles.taxiPill}><strong>Domain:</strong> {displayDomain}</span>
              <span style={styles.taxiPill}><strong>Tool:</strong> {displayTool}</span>
              <span style={styles.taxiPill}><strong>Skill:</strong> {displaySkill}</span>
            </div>
          </div>

          {/* Right Column: Quiz Evaluation Validation Footprint */}
          <div style={styles.detailPanelCard}>
            <h3 style={styles.panelTitle}>Validation Footprint</h3>
            <div style={styles.metricScoreBox}>
              <div style={styles.scoreCircle}>
                <span style={styles.scoreNum}>3/3</span>
                <span style={styles.scoreLabel}>Score</span>
              </div>
              <div style={styles.scoreSummaryText}>
                <p style={styles.summaryHeading}><FaMedal style={{ color: '#d29922' }} /> Perfect Pass</p>
                <p style={styles.summaryDesc}>User successfully resolved all dynamic competency context queries generated for this log signature profile.</p>
              </div>
            </div>

            {/* Micro-Audit List */}
            <div style={styles.auditList}>
              <div style={styles.auditItem}>
                <span style={styles.auditIndicatorPass}>✓</span>
                <span style={styles.auditText}>Structural lock-safety parameters validated</span>
              </div>
              <div style={styles.auditItem}>
                <span style={styles.auditIndicatorPass}>✓</span>
                <span style={styles.auditText}>Dynamic storage performance calculations answered</span>
              </div>
              <div style={styles.auditItem}>
                <span style={styles.auditIndicatorPass}>✓</span>
                <span style={styles.auditText}>Input injection hazard edge cases correctly defended</span>
              </div>
            </div>
          </div>

        </div>

        {/* Workspace Redirect Action Tray Footer */}
        <div style={styles.actionTrayFooter}>
          <button onClick={onBackToHub} style={styles.secondaryBtn}>
            Return to Ledger Profile Hub
          </button>
          <button onClick={() => alert("Unique public URL link copied to clipboard.")} style={styles.primaryBtn}>
            <FaShareAlt style={{ marginRight: '8px' }} /> Share Public Verification Link
          </button>
        </div>

      </div>
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: '#0d1117',
    padding: '32px 24px',
    display: 'flex',
    justifyContent: 'center',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
  },
  vaultWrapper: {
    width: '100%',
    maxWidth: '840px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  successBanner: {
    backgroundColor: 'rgba(35, 134, 54, 0.15)',
    border: '1px solid rgba(56, 139, 253, 0.1)',
    borderRadius: '8px',
    padding: '32px 24px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px'
  },
  mainCheckIcon: {
    fontSize: '48px',
    color: '#39d353'
  },
  bannerHeading: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#f0f6fc',
    margin: '0'
  },
  bannerSubtext: {
    fontSize: '14px',
    color: '#8b949e',
    margin: '0'
  },
  ledgerInfoCard: {
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '16px 20px'
  },
  infoRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px'
  },
  infoBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  infoLabel: {
    fontSize: '11px',
    color: '#8b949e',
    textTransform: 'uppercase',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  infoValue: {
    fontSize: '14px',
    color: '#c9d1d9',
    fontWeight: '500'
  },
  infoValueCode: {
    fontSize: '13px',
    color: '#58a6ff',
    fontFamily: 'SFMono-Regular, Consolas, monospace',
    fontWeight: '500'
  },
  splitDetailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
    gap: '24px'
  },
  detailPanelCard: {
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  panelTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#f0f6fc',
    margin: '0',
    borderBottom: '1px solid #21262d',
    paddingBottom: '10px'
  },
  assertionBox: {
    backgroundColor: '#0d1117',
    border: '1px solid #21262d',
    borderRadius: '6px',
    padding: '16px',
    minHeight: '100px',
    display: 'flex',
    alignItems: 'center'
  },
  assertionText: {
    fontSize: '14px',
    color: '#c9d1d9',
    lineHeight: '1.5',
    margin: '0',
    fontStyle: 'italic'
  },
  taxonomyPillsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px'
  },
  taxiPill: {
    fontSize: '12px',
    color: '#c9d1d9',
    backgroundColor: '#21262d',
    padding: '4px 10px',
    borderRadius: '12px',
    border: '1px solid #30363d'
  },
  metricScoreBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    backgroundColor: '#0d1117',
    padding: '16px',
    borderRadius: '6px',
    border: '1px solid #21262d'
  },
  scoreCircle: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    border: '3px solid #238636',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  scoreNum: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#39d353'
  },
  scoreLabel: {
    fontSize: '9px',
    color: '#8b949e',
    textTransform: 'uppercase'
  },
  scoreSummaryText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  summaryHeading: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#f0f6fc',
    margin: '0',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  summaryDesc: {
    fontSize: '12px',
    color: '#8b949e',
    margin: '0',
    lineHeight: '1.4'
  },
  auditList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginTop: '4px'
  },
  auditItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '13px',
    color: '#c9d1d9'
  },
  auditIndicatorPass: {
    color: '#39d353',
    fontWeight: 'bold'
  },
  actionTrayFooter: {
    borderTop: '1px solid #30363d',
    paddingTop: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  secondaryBtn: {
    background: 'none',
    border: '1px solid #30363d',
    borderRadius: '6px',
    color: '#c9d1d9',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  primaryBtn: {
    backgroundColor: '#21262d',
    border: '1px solid #30363d',
    borderRadius: '6px',
    color: '#c9d1d9',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.2s'
  }
};